import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  ButtonGroup,
  Button,
  Drawer,
  useMediaQuery,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  TablePagination,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  FilterList,
  HourglassEmpty,
  Refresh,
  Replay,
  Sync,
} from '@mui/icons-material';

import { useDownloadQueue, QUICK_FILTER_LABELS, QuickFilter } from './hooks/useDownloadQueue';
import { COLORS, STATUS_CONFIG } from './_components/Constants';
import FiltersSidebar from './_components/FiltersSidebar';
import { IDownloadQueueItemProps } from '../../../services/DownloadQueue/types';

const {
  backgroundColorDarkTheme,
  tableBackgroundColor,
  tableHeaderColor,
  borderColorDarkTheme,
  fontColorDarkTheme,
} = COLORS;

const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString('pt-BR') : '-');

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}

const StatCard = ({ label, value, color, icon }: StatCardProps) => (
  <Box
    sx={{
      flex: '1 1 160px',
      minWidth: 160,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 2,
      borderRadius: 3,
      backgroundColor: tableBackgroundColor,
      border: `1px solid ${borderColorDarkTheme}`,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${color}22`,
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'gray', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const DownloadQueuePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { state, actions } = useDownloadQueue();

  const sidebar = (
    <FiltersSidebar
      devices={state.devices}
      deviceId={state.filters.deviceId}
      status={state.filters.status}
      search={state.filters.search}
      startDate={state.filters.startDate}
      endDate={state.filters.endDate}
      isLoading={state.isLoading || state.isFetching}
      onDeviceChange={actions.setDeviceId}
      onStatusToggle={(status) => actions.setStatus((prev) => (prev === status ? undefined : status))}
      onSearchChange={actions.setSearch}
      onStartDateChange={(date) => {
        actions.setStartDate(date);
        actions.setActiveQuickFilter('personalizado');
      }}
      onEndDateChange={(date) => {
        actions.setEndDate(date);
        actions.setActiveQuickFilter('personalizado');
      }}
      onApply={actions.applyFilters}
      onClear={actions.handleClearFilters}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        backgroundColor: backgroundColorDarkTheme,
        color: fontColorDarkTheme,
      }}
    >
      {isMobile ? (
        <Drawer
          anchor="left"
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          slotProps={{ paper: { sx: { width: 260, backgroundColor: tableHeaderColor } } }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Box sx={{ width: 260, flexShrink: 0, borderRight: `1px solid ${borderColorDarkTheme}`, backgroundColor: tableHeaderColor }}>
          {sidebar}
        </Box>
      )}

      <Box sx={{ flex: 1, p: 4, overflowY: 'auto' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Fila de Downloads
              </Typography>
              <Typography variant="body2" sx={{ color: 'gray' }}>
                Status real dos downloads de vídeo no device-server, atualizado automaticamente.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setIsFilterOpen(true)}
                  sx={{ textTransform: 'none', borderColor: borderColorDarkTheme, color: 'gray' }}
                >
                  Filtros
                </Button>
              )}

              <ButtonGroup variant="outlined" size="small" sx={{ '& .MuiButton-root': { borderColor: borderColorDarkTheme, color: 'gray', textTransform: 'none' } }}>
                {(['hoje', '7d', 'todos'] as QuickFilter[]).map((preset) => (
                  <Button
                    key={preset}
                    onClick={() => actions.handleQuickDateFilter(preset)}
                    sx={{
                      backgroundColor: state.filters.activeQuickFilter === preset ? 'rgba(88, 166, 255, 0.1)' : 'transparent',
                      color: state.filters.activeQuickFilter === preset ? '#58a6ff !important' : 'inherit',
                    }}
                  >
                    {QUICK_FILTER_LABELS[preset]}
                  </Button>
                ))}
              </ButtonGroup>

              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={actions.handleRefresh}
                disabled={state.isFetching}
                sx={{ textTransform: 'none' }}
              >
                Atualizar
              </Button>
            </Box>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
            <StatCard label="Na fila" value={state.statusCounts.queued} color="#58a6ff" icon={<HourglassEmpty fontSize="small" />} />
            <StatCard label="Processando" value={state.statusCounts.processing} color="#ff9800" icon={<Sync fontSize="small" />} />
            <StatCard label="Concluído" value={state.statusCounts.completed} color="#4caf50" icon={<CheckCircleOutline fontSize="small" />} />
            <StatCard label="Falhou" value={state.statusCounts.failed} color="#f44336" icon={<ErrorOutline fontSize="small" />} />
          </Stack>

          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: tableBackgroundColor,
              borderRadius: 3,
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
              border: `1px solid ${borderColorDarkTheme}`,
              overflowX: 'auto',
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  {[
                    { label: 'Dispositivo', width: '18%' },
                    { label: 'Canal', width: '8%' },
                    { label: 'Trecho (início - fim)', width: '20%' },
                    { label: 'Status', width: '14%' },
                    { label: 'Enfileirado em', width: '16%' },
                    { label: 'Tentativas', width: '10%', align: 'center' as const },
                    { label: 'Ações', width: '14%', align: 'right' as const },
                  ].map((col) => (
                    <TableCell
                      key={col.label}
                      align={col.align || 'left'}
                      sx={{ width: col.width, backgroundColor: tableHeaderColor, color: 'gray', fontWeight: 'bold', borderBottom: `1px solid rgba(255,255,255,0.1)` }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, borderBottom: 'none' }}>
                      <CircularProgress size={30} sx={{ color: fontColorDarkTheme }} />
                    </TableCell>
                  </TableRow>
                ) : state.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: fontColorDarkTheme, borderBottom: 'none' }}>
                      Nenhum download encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  state.items.map((item: IDownloadQueueItemProps) => {
                    const statusCfg = STATUS_CONFIG[item.status] ?? { label: item.status, color: 'default' as any };
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(255,255,255,0.02)' },
                          transition: 'background-color 0.15s',
                        }}
                      >
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {item.deviceName || `Dispositivo ${item.deviceId}`}
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {item.channel}
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)`, fontSize: 12 }}>
                          {formatDateTime(item.startTime)} → {formatDateTime(item.endTime)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <Tooltip title={item.errorMessage || ''}>
                            <Chip size="small" label={statusCfg.label} color={statusCfg.color} sx={{ borderRadius: 1.5 }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {formatDateTime(item.enqueuedAt)}
                        </TableCell>
                        <TableCell align="center" sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {item.retryCount}
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {item.status === 'Failed' && (
                            <Tooltip title="Refazer download">
                              <IconButton size="small" onClick={() => actions.handleRetry(item.id)} disabled={state.isRetrying}>
                                <Replay fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={state.totalCount}
              page={state.pagination.pageNumber - 1}
              rowsPerPage={state.pagination.pageSize}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={actions.handleChangePage}
              onRowsPerPageChange={actions.handleChangeRowsPerPage}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{ color: fontColorDarkTheme, borderTop: `1px solid ${borderColorDarkTheme}` }}
            />
          </TableContainer>

          <Typography variant="caption" sx={{ color: 'gray', mt: 1, display: 'block' }}>
            Atualiza automaticamente a cada 15s.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DownloadQueuePage;
